// src/features/operational/components/teaching/LessonList.js
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Button
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  Assessment as AssessmentIcon,
  Bookmark as BookmarkIcon,
  InsertDriveFile as FileIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const LessonList = ({ lessons = [], onAddLesson }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  
  const handleMenuOpen = (event, lessonId) => {
    setAnchorEl(event.currentTarget);
    setSelectedLessonId(lessonId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedLessonId(null);
  };
  
  // Group lessons by term and week
  const groupedLessons = lessons.reduce((acc, lesson) => {
    const term = lesson.term;
    const date = new Date(lesson.date);
    const weekNumber = getWeekNumber(date);
    const weekKey = `Week ${weekNumber}`;
    
    if (!acc[term]) {
      acc[term] = {};
    }
    
    if (!acc[term][weekKey]) {
      acc[term][weekKey] = [];
    }
    
    acc[term][weekKey].push(lesson);
    return acc;
  }, {});
  
  // Function to get week number
  function getWeekNumber(date) {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }
  
  const getStatusColor = (status) => {
    switch(status) {
      case 'Ahead': return '#4caf50';
      case 'Behind': return '#f44336';
      default: return '#ff9800';
    }
  };
  
  const getEngagementColor = (level) => {
    switch(level) {
      case 'High': return '#4caf50';
      case 'Low': return '#f44336';
      default: return '#ff9800';
    }
  };
  
  return (
    <Box>
      {Object.keys(groupedLessons).length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="textSecondary" mb={2}>
            No lessons found for the selected criteria
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddLesson}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Record Your First Lesson
          </Button>
        </Paper>
      ) : (
        Object.keys(groupedLessons).sort().map(term => (
          <Box key={term} mb={4}>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              Term {term}
            </Typography>
            
            {Object.keys(groupedLessons[term]).sort().map(week => (
              <Box key={week} mb={3}>
                <Box display="flex" alignItems="center" mb={1}>
                  <CalendarIcon sx={{ mr: 1, color: THEME_COLORS.primary }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    {week}
                  </Typography>
                </Box>
                
                <Grid container spacing={2}>
                  {groupedLessons[term][week].sort((a, b) => new Date(a.date) - new Date(b.date)).map(lesson => (
                    <Grid item xs={12} sm={6} md={4} key={lesson.id}>
                      <Card 
                        elevation={1} 
                        sx={{ 
                          borderRadius: 2,
                          position: 'relative',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '4px',
                            backgroundColor: getStatusColor(lesson.atpStatus)
                          }} 
                        />
                        <CardContent sx={{ p: 2 }}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                              {lesson.topic}
                            </Typography>
                            <IconButton 
                              size="small" 
                              sx={{ ml: 1 }}
                              onClick={(e) => handleMenuOpen(e, lesson.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mb={1}>
                            <SchoolIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {lesson.subject.name} - Grade {lesson.grade}
                            </Typography>
                          </Box>
                          
                          <Box display="flex" alignItems="center" mb={1}>
                            <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(lesson.date).toLocaleDateString()} | 
                            </Typography>
                            <ScheduleIcon fontSize="small" sx={{ mx: 0.5, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {lesson.time}
                            </Typography>
                          </Box>
                          
                          <Divider sx={{ my: 1 }} />
                          
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box display="flex" alignItems="center">
                                <GroupsIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {lesson.actualAttendance}/{lesson.expectedAttendance}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={6}>
                              <Box display="flex" alignItems="center">
                                <AssessmentIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" color="text.secondary">
                                  {lesson.lessonProgress}%
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <Box mt={1} display="flex" flexWrap="wrap" gap={0.5}>
                                {lesson.assessmentType && (
                                  <Chip 
                                    label={lesson.assessmentType} 
                                    size="small"
                                    icon={<AssignmentIcon />}
                                    sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }}
                                  />
                                )}
                                
                                {lesson.hasHomework && (
                                  <Chip 
                                    label="Homework" 
                                    size="small"
                                    icon={<BookmarkIcon />}
                                    sx={{ bgcolor: 'rgba(244, 67, 54, 0.08)', color: '#f44336' }}
                                  />
                                )}
                                
                                {lesson.lessonPlan && (
                                  <Chip 
                                    label="Lesson Plan" 
                                    size="small"
                                    icon={<FileIcon />}
                                    sx={{ bgcolor: 'rgba(33, 150, 243, 0.08)', color: '#2196f3' }}
                                  />
                                )}
                                
                                <Chip 
                                  label={lesson.learnerEngagement} 
                                  size="small"
                                  sx={{ 
                                    bgcolor: 'rgba(0, 0, 0, 0.04)', 
                                    color: getEngagementColor(lesson.learnerEngagement)
                                  }}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        ))
      )}
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Lesson</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Lesson</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default LessonList;